import { Component, OnInit, OnDestroy, ViewEncapsulation, Input, OnChanges, SimpleChanges } from '@angular/core';
import { formatDate, formatNumber } from '@angular/common';
import { Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { Title } from '@angular/platform-browser';

import { environment } from 'src/environments/environment';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data';
import { GraphData, DailyGraphData } from 'src/app/shared/dashboard';
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service';
import { RestApiService } from 'src/app/services/rest-api.service';

@Component({
  selector: 'app-home-chart-performance-sales',
  templateUrl: './home-chart-performance-sales.component.html',
  styleUrls: ['./home-chart-performance-sales.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class HomeChartPerformanceSalesComponent implements OnInit, OnDestroy, OnChanges {

  @Input() today: Date = new Date();

  @Input() days: number = 45;

  @Input() clean: boolean = true;

  globalSettinsData: GlobalSettingsData = null;

  dailyPerformance: DailyGraphData[] = [];
  dataDailyPerformance: GraphData = null;

  loading: boolean = false;

  /** Emits once on component destroy to complete all takeUntil streams */
  private destroy$ = new Subject<void>();

  data: GraphData = null;

  dailyPerformanceSubj$ = new Subject<{
    CLIID: number;
    DATETO: Date;
    DEEP: number;
    DTYPE: number;
    CURRID: number;
    DOINV: number;
  }>();

  /**
   * Chart.js v3+ compatible options.
   * - scales: keys must be axis IDs referenced by yAxisID in datasets ('y' = left, 'y1' = right)
   * - tooltips renamed to plugins.tooltip in Chart.js v3
   */
  dailyPerformanceOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 0
        }
      },
      // Left Y axis — payments & averages (yAxisID: 'y')
      y: {
        type: 'linear',
        position: 'left',
        beginAtZero: true,
        ticks: {
          beginAtZero: true
        }
      },
      // Right Y axis — daily invoices total (yAxisID: 'y1')
      y1: {
        type: 'linear',
        position: 'right',
        beginAtZero: true,
        grid: {
          color: 'red',
          borderColor: 'darkred',
          tickColor: 'grey',
          // Prevent grid lines from overlapping the left axis grid
          drawOnChartArea: false
        },
        ticks: {
          beginAtZero: true
        }
      }
    },
    plugins: {
      // Chart.js v3: tooltips -> plugins.tooltip
      tooltip: {
        enabled: true,
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: (tooltipItem: any) => {
            return tooltipItem.dataset.label + ': $' + formatNumber(<number>tooltipItem.raw, 'uk', '1.0-0');
          }
        }
      },
      legend: {
        display: true
      }
    }
  };

  constructor(
    private restapi: RestApiService,
    private messageService: MessageService,
    private globalsettingsservice: GetglobalsettingsService,
    private titleService: Title,
  ) { }

  private subscribeDailyPerformanceGraphData(): void {
    this.dailyPerformanceSubj$.pipe(
      switchMap((row) => {
        environment.production || console.log('*** subscribeDailyPerformanceGraphData switchMap: ' + JSON.stringify(row));
        this.loading = true;
        return this.restapi.getDailyPaymentsGraphData(
          row.CLIID,
          row.DATETO,
          row.DEEP,
          row.DTYPE,
          row.CURRID,
          row.DOINV
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (data) => {
        this.dailyPerformance = data;
        this.loading = false;

        if (!this.dailyPerformance || this.dailyPerformance.length === 0) {
          environment.production || console.warn('*** subscribeDailyPerformanceGraphData: no data returned');
          this.dataDailyPerformance = null;
          return;
        }

        this.dataDailyPerformance = {
          labels: [],
          datasets: [
            {
              label: 'Оплаты',
              yAxisID: 'y',         // Left axis
              data: [],
              fill: false,
              backgroundColor: '#277622'
            },
            {
              label: 'Среднее',
              yAxisID: 'y',         // Left axis
              data: [],
              type: 'line',
              fill: false,
              borderColor: '#105702',
              backgroundColor: '#111111',
              tension: .4,
            },
            {
              label: 'Сумма счетов в день',
              yAxisID: 'y1',        // Right axis
              data: [],
              fill: false,
              backgroundColor: '#900C3F'
            },
            {
              label: 'Счетов за день в среднем',
              yAxisID: 'y',         // Left axis
              data: [],
              type: 'line',
              fill: false,
              borderColor: '#7F4602',
              backgroundColor: '#121212',
              tension: .4,
            }
          ],
          options: {}
        };

        this.dailyPerformance.forEach((element) => {
          this.dataDailyPerformance.labels.push(formatDate(element.GRAFDATE, 'dd/MM', 'uk'));
          this.dataDailyPerformance.datasets[0].data.push(element.GRAFVALUE);
          this.dataDailyPerformance.datasets[1].data.push(element.MEDIUMVAL);
          this.dataDailyPerformance.datasets[2].data.push(element.DAILYINV);
          this.dataDailyPerformance.datasets[3].data.push(element.MEDIUMINV);
        });
      },
      error: (err) => {
        console.error('*** subscribeDailyPerformanceGraphData error:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Ошибка загрузки данных',
          detail: err?.message ?? 'Не удалось загрузить данные графика продаж'
        });
        this.dataDailyPerformance = null;
        this.loading = false;
      }
    });

    this.dailyPerformanceSubj$.next({
      CLIID: this.globalsettingsservice.getMainCompanyId(),
      DATETO: this.today,
      DEEP: this.days,
      DTYPE: 1,
      CURRID: this.globalsettingsservice.getBalanceCurrencyId(),
      DOINV: 1
    });
  }

  private emitDailyPerformanceQuery(): void {
    if (!this.globalSettinsData) {
      return;
    }

    this.dailyPerformanceSubj$.next({
      CLIID: this.globalsettingsservice.getMainCompanyId(),
      DATETO: this.today,
      DEEP: this.days,
      DTYPE: 1,
      CURRID: this.globalsettingsservice.getBalanceCurrencyId(),
      DOINV: 1
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    environment.production || console.log('*** HomeChartPerformanceSalesComponent ngOnChanges');

    for (const propName in changes) {
      switch (propName) {
        case 'clean':
        case 'today':
        case 'days':
          this.emitDailyPerformanceQuery();
          break;
      }
    }
  }

  ngOnDestroy(): void {
    console.log('*** HomeChartPerformanceSalesComponent in ngOnDestroy');

    // Completes all takeUntil(destroy$) subscriptions — no manual unsubscribe needed
    this.destroy$.next();
    this.destroy$.complete();

    // Complete the subject to avoid memory leaks
    this.dailyPerformanceSubj$.complete();
  }

  ngOnInit(): void {
    console.log('*** HomeChartPerformanceSalesComponent in ngOnInit');

    this.today = new Date();

    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

    if (!this.globalSettinsData) {
      console.error('*** HomeChartPerformanceSalesComponent: global settings not available');
      this.messageService.add({
        severity: 'warn',
        summary: 'Настройки недоступны',
        detail: 'Глобальные настройки не загружены. Данные графика могут быть недоступны.'
      });
    }

    this.titleService.setTitle('Платежи - продажи с усреднением и суммированием');

    this.subscribeDailyPerformanceGraphData();
  }

  selectData(event: { element: { _datasetIndex: number; _index: number } }): void {
    if (!this.data?.datasets) {
      console.warn('*** selectData: no data available');
      return;
    }

    const dataset = this.data.datasets[event.element._datasetIndex];
    if (!dataset?.data) {
      console.warn('*** selectData: dataset or data array is undefined');
      return;
    }

    const value = dataset.data[event.element._index];
    if (value == null) {
      console.warn('*** selectData: selected value is null or undefined');
      return;
    }

    this.messageService.add({
      severity: 'info',
      summary: 'Data Selected',
      detail: value.toString()
    });
  }
}
